import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { Button, Flex, Tabs } from 'antd';

import lookup from 'country-code-lookup';
import dayjs from 'dayjs';

import axios from '../../../axios';

import messages from '../../../constants/messages';
import { ROLES_TO_SHOW_SUBSCRIPTION_TAB } from '../../../constants/role';

import { useModal } from '../../../hooks/useModal';
import { useQuery } from '../../../hooks/useQuery';
import { useUnsavedChangesPrompt } from '../../../hooks/useUnsavedChangesPrompt';

import { getFeatureOptions, getPlans } from '../../../redux/actions/subscription';
import { getUserReq } from '../../../redux/actions/user';

import AppModal from '../../../components/AppModal';
import CancelSaveButtons from '../../../components/CancelSaveButtons';
import LoadingBar from '../../../components/LoadingBar';
import CloseAccountModal from '../../../components/Modals/CloseAccountModal';
import SubscriptionTable from '../../../components/SubscriptionTable';
import { errorToastHandler, successToastHandler, warnToastHandler } from '../../../components/action_notifier';
import UnsavedChangesModal from '../../../components/ui/UnsavedChangesModal';

import AppDropdown from '../../ui-components/AppDropdown';

import styles from './styles.module.scss';

import countries from '../../../countries.json';
import ProfileCard from './ProfileCard';
import ProfileContainer from './ProfileContainer';
import ProfileForm from './ProfileForm';
import { getEmptyUser } from './constants';

const Billing = lazy(() => import('./Billing'));
const Invitations = lazy(() => import('../../../components/Invitations'));
const Invoices = lazy(() => import('../../../components/Invoices'));

const DEFAULT_TAB = 'profile';

export default function Profile() {
  const dispatch = useDispatch();
  const history = useHistory();
  const query = useQuery();
  const edit = query.get('edit');
  const tab = query.get('tab');
  const plan = query.get('plan');

  const formPasswordRef = useRef(null);
  const billingFormRef = useRef(null);
  const formProfileRef = useRef(null);

  const { myPlan, loading: subscriptionLoading } = useSelector(state => state.subscription);
  const { user, loading } = useSelector(state => state.user);

  const [planModalVisible, openPlanModal, closePlanModal] = useModal();
  const [confirmModalVisible, openConfirmModal, closeConfirmModal] = useModal();

  const [activeTab, setActiveTab] = useState(tab || DEFAULT_TAB);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [submittable, setSubmittable] = useState(false);
  const [state, setState] = useState({
    defaultCountryCode: '',
    editMode: false,
    file: null,
    user: getEmptyUser(),
    user_edit: {
      ...getEmptyUser(),
      new_password: null,
      old_password: null,
      password: null,
      password_confirm: null
    }
  });

  const countriesList = useMemo(() => countries.map(c => ({ value: c, label: c })), [countries]);

  useEffect(() => {
    fetchData();

    if (edit === 'true') {
      setState(prev => ({ ...prev, editMode: true }));
    }

    if (plan === 'true') {
      openPlanModal();
    }
  }, []);

  useEffect(() => {
    setUser(user);
  }, [user]);

  async function fetchData() {
    try {
      await Promise.all([dispatch(getUserReq()), dispatch(getPlans()), dispatch(getFeatureOptions())]);
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.FAILED_ON_FETCH_DATA);
    }
  }

  const tabsItems = useMemo(() => {
    const profileTab = {
      key: 'profile',
      label: 'Profile',
      children: (
        <Suspense fallback={<LoadingBar />}>
          <ProfileForm
            countries={countriesList}
            editMode={state.editMode}
            formRef={formProfileRef}
            user={user}
            user_edit={state.user_edit}
            cancelEditing={cancelEditing}
            onChange={setIsFormDirty}
            onChangeAddress={changeAddress}
            onUserEditChange={onUserEditChange}
            saveProfile={saveProfile}
            setSubmittable={setSubmittable}
          />
        </Suspense>
      )
    };
    const subscriptionTab = {
      key: 'billing',
      label: 'Billing',
      children: (
        <Suspense fallback={<LoadingBar />}>
          <Billing
            countries={countriesList}
            formRef={billingFormRef}
            editMode={state.editMode}
            user_edit={state.user_edit}
            multiselectChange={multiselectChange}
            onChange={setIsFormDirty}
            onChangeAddress={changeAddress}
            setSubmittable={setSubmittable}
          />
        </Suspense>
      )
    };
    const linkedAccountsTab = {
      key: 'linked-users',
      label: 'Linked users',
      children: (
        <Suspense fallback={<LoadingBar />}>
          <Invitations />
        </Suspense>
      )
    };

    const invoicesTab = {
      key: 'invoices',
      label: 'Invoices',
      children: (
        <Suspense fallback={<LoadingBar />}>
          <Invoices />
        </Suspense>
      )
    };

    const availableTabs = [profileTab];
    const isUserLinked = user?.is_linked;

    if (user?.roles?.some(role => ROLES_TO_SHOW_SUBSCRIPTION_TAB.includes(role.name)) && !isUserLinked) {
      availableTabs.push(subscriptionTab, invoicesTab);
    }

    if (!isUserLinked) {
      availableTabs.push(linkedAccountsTab);
    }

    return availableTabs;
  }, [state.editMode, state.user_edit, state.defaultCountryCode, user]);

  function setUser(user) {
    if (!user) return;

    const mappedUser = {
      ...user,
      address: user.address ?? {},
      billingAddress: user.billingAddress ?? {},
      full_address: user?.address?.full_address ? user?.address?.full_address : '/'
    };

    setState(prevState => ({
      ...prevState,
      user: mappedUser,
      user_edit: { ...prevState.user_edit, ...mappedUser, address: { ...user.address } }
    }));
  }

  function changeAddress(value, field, type = 'address') {
    setState(prev => ({
      ...prev,
      user_edit: {
        ...prev.user_edit,
        [type]: {
          ...prev.user_edit[type],
          [field]: value
        }
      }
    }));
  }

  function setDefaultCountryCode(country) {
    const countryData = lookup.byCountry(country);

    setState(prevState => ({
      ...prevState,
      defaultCountryCode: countryData?.iso2?.toLowerCase()
    }));
  }

  function multiselectChange(sle = {}, type = 'address') {
    const user_edit = structuredClone(state.user_edit);
    user_edit[type].country = sle.value;
    user_edit[type].city = null;

    setState(prevState => ({ ...prevState, user_edit }));

    if (type === 'address') setDefaultCountryCode(sle.value);
  }

  function onUserEditChange(value, field) {
    if (field === 'is_email_notify_enabled' || field === 'is_sms_notify_enabled') {
      value = !state.user_edit[field];
    }

    setState(prevState => ({ ...prevState, user_edit: { ...prevState.user_edit, [field]: value } }));
  }

  function areFieldsValid(fields) {
    for (const item of fields) {
      if (!item.value) {
        errorToastHandler(messages.FIELD_REQUIRED(item.title));

        return false;
      }
    }

    return true;
  }

  async function updateUserNotificationPreferences() {
    const user_edit = structuredClone(state.user_edit);

    const notificationPreferences = (user_edit.notificationPreferences ?? []).map(pref => ({
      id: pref.id,
      sms_enabled: !!pref.sms_enabled,
      email_enabled: !!pref.email_enabled,
      push_enabled: !!pref.push_enabled
    }));

    await axios.put('/users/me/notification-preferences', notificationPreferences);
  }

  async function saveProfile() {
    const user_edit = structuredClone(state.user_edit);

    const fields = [
      { value: user_edit.first_name, title: 'First name' },
      { value: user_edit.last_name, title: 'Last name' },
      { value: user_edit.email, title: 'Email' },
      { value: user_edit.backup_email, title: 'Backup email' },
      { value: user_edit.work_phone, title: 'Work phone' },
      { value: user_edit.address.city, title: 'City' },
      { value: user_edit.address.country, title: 'Country' },
      { value: user_edit.address.postal_code, title: 'Postal code' },
      { value: user_edit.address.street, title: 'Street' },
      { value: user_edit.address.house_number, title: 'House number' },
      {
        value: user_edit.notify_phone,
        title: 'Mobile number for SMS notifications'
      }
    ];
    const isValid = areFieldsValid(fields);

    if (!isValid) return;

    user_edit.address.full_address =
      user_edit?.address?.full_address && user_edit?.address?.full_address.length > 0
        ? user_edit?.address?.full_address
        : null;
    user_edit.billingAddress.full_address = null;

    try {
      await axios.put('users/me', user_edit);
      await updateUserNotificationPreferences();

      if (state.file) {
        await axios.put('users/me/profile_picture', { profile_picture: state.file });
      }

      await dispatch(getUserReq());
      setIsFormDirty(false);
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.FAILED_ON_FETCH_DATA);
    }

    setState(prevState => ({ ...prevState, file: '', editMode: false }));

    successToastHandler(messages.UPDATED);
  }

  function onClickEditAction() {
    setState(prevState => ({ ...prevState, editMode: true }));
  }

  function onClickCloseAccountAction() {
    openConfirmModal();
  }

  async function onClickKeepAccountAction() {
    const resp = await axios.put('users/keep');
    successToastHandler(resp.data.message);
    dispatch(getUserReq());
  }

  async function closeAccount() {
    const { data } = await axios.delete('users/close');
    await dispatch(getUserReq());
    successToastHandler(data?.message);
    closeConfirmModal();
  }

  function cancelEditing() {
    setState(prevState => ({ ...prevState, file: null, editMode: false, user_edit: { ...user } }));

    formPasswordRef.current?.resetFields();
    formProfileRef.current?.resetFields();
    billingFormRef.current?.resetFields();
  }

  async function setAvatar(file) {
    setIsFormDirty(true);
    setState(state => ({ ...state, file }));
  }

  let closeAccDescription = `If you close your account, you will lose all access, and your 
    account and data will be lost. To continue, select the button below.`;

  if (myPlan?.id && myPlan?.status === 'active') {
    const endDate = dayjs(myPlan?.end_date).format('dddd, DD MMMM YYYY');
    closeAccDescription = `If you close your account, you will lose all access, and your account and data will be lost. 
        Your current subscription and access will continue until ${endDate}. 
        Your account will be closed after this date. To continue, select the button below.`;
  }

  function changeTab(key) {
    history.replace(`/profile?tab=${key}`);
    setActiveTab(key);
  }

  const actions = [
    {
      label: (
        <Button color="default" size="small" variant="link" onClick={openPlanModal}>
          View my plan
        </Button>
      )
    },
    {
      label: (
        <Button color="default" size="small" variant="link" onClick={onClickEditAction}>
          Edit profile
        </Button>
      )
    },
    {
      label: (
        <Button color="default" size="small" variant="link" disabled={user.close} onClick={onClickCloseAccountAction}>
          Close my account
        </Button>
      )
    },
    {
      label: (
        <Button color="default" size="small" variant="link" disabled={!user.close} onClick={onClickKeepAccountAction}>
          Keep my account
        </Button>
      )
    }
  ];

  const { NavigationPrompt, modalProps, nextLocation, confirmed, triggerUnsavedChangesPrompt } =
    useUnsavedChangesPrompt({
      when: isFormDirty,
      onSaveAndLeave: async () => {
        if (submittable) {
          await saveProfile();

          return true;
        } else {
          warnToastHandler(messages.DATA_REQUIRED);
          formPasswordRef.current?.validateFields();
          formProfileRef.current?.validateFields();

          return false;
        }
      },
      onDiscardAndLeave: () => {
        setIsFormDirty(false);
        cancelEditing();
      }
    });

  useEffect(() => {
    if (!isFormDirty && nextLocation) {
      history.push(`${nextLocation.pathname}${nextLocation.search}`);
    }
  }, [isFormDirty, nextLocation, history]);

  const handleCancel = useCallback(() => {
    isFormDirty ? triggerUnsavedChangesPrompt() : cancelEditing();
  }, [isFormDirty]);

  const extra = (
    <>
      {state.editMode ? (
        <CancelSaveButtons disabled={!submittable} handleCancel={handleCancel} handleSave={saveProfile} />
      ) : (
        <AppDropdown label="Actions" items={actions} />
      )}
    </>
  );

  if (loading || subscriptionLoading) {
    return <LoadingBar fullscreen={true} />;
  }

  return (
    <>
      {NavigationPrompt}
      <ProfileContainer title="My Profile" extra={extra}>
        <Flex vertical={false} wrap>
          <Flex className={styles['left-column']}>
            <ProfileCard
              avatarUrl={user?.profile_picture_url}
              editMode={state.editMode}
              file={state.file}
              formRef={formPasswordRef}
              user={user}
              handleChange={setAvatar}
              onChange={setIsFormDirty}
              onUserEditChange={onUserEditChange}
              setSubmittable={setSubmittable}
            />
          </Flex>
          <Flex className={styles['right-column']}>
            <Tabs
              rootClassName="w-full px-4 py-3"
              type="card"
              defaultActiveKey={activeTab}
              items={tabsItems}
              onChange={changeTab}
            />
          </Flex>
        </Flex>
        {state.editMode && (
          <Flex justify="end">
            <CancelSaveButtons disabled={!submittable} handleCancel={handleCancel} handleSave={saveProfile} />
          </Flex>
        )}
      </ProfileContainer>

      <CloseAccountModal
        description={closeAccDescription}
        isOpen={confirmModalVisible}
        onCancel={closeConfirmModal}
        onSubmit={closeAccount}
      />

      <AppModal
        cancelButtonText="Close"
        size="lg"
        title="Subscriptions"
        confirmButtonHidden={true}
        isOpen={planModalVisible}
        handleCancel={closePlanModal}>
        <SubscriptionTable closePlanModal={closePlanModal} />
      </AppModal>

      <UnsavedChangesModal
        isOpen={modalProps.visible}
        handleCancel={modalProps.onCancel}
        handleConfirm={modalProps.onSave}
        handleDiscard={modalProps.onDiscard}
      />
    </>
  );
}
